import React, { useState, useCallback, useEffect } from 'react'
import { Modal } from '@usap/uicomponents'
import { useTokenDispatch } from '../../../../../contexts/token.context'
import { useShoppingCartContext } from '../../../providers/ShoppingCartProvider'
import { useProfileDispatch } from '../../../../../contexts/profile.context'
import SignInForm from './SignInForm'
import CreateAccountForm from './CreateAccountForm'
import EmailVerification from './EmailVerification'
import { ModalTitle, VerificationHeadline } from './LoyaltySignInModal.css'

type View = 'signin' | 'createaccount' | 'verification'

type LoyaltySignInModalProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const SIGNIN_HEADER = <ModalTitle>Sign In</ModalTitle>
const CREATE_ACCOUNT_HEADER = <ModalTitle>Create Account</ModalTitle>
const VERIFICATION_HEADER = <VerificationHeadline>Almost There! Verify Your Email to Earn 25 Points.</VerificationHeadline>

const LoyaltySignInModal: React.FC<LoyaltySignInModalProps> = ({ open, onOpenChange }) => {
  const [view, setView] = useState<View>('signin')
  const [verificationEmail, setVerificationEmail] = useState('')

  const setToken = useTokenDispatch()
  const { setLoginTokenForMerging } = useShoppingCartContext()
  const setProfile = useProfileDispatch()

  // Ensure JSEncrypt is loaded for RSA password encryption (not present on cart page by default)
  useEffect(() => {
    if (typeof window !== 'undefined' && !window.JSEncrypt) {
      const script = document.createElement('script')
      script.setAttribute('type', 'text/javascript')
      script.src = '/static/assets/js/jsencrypt.min.js'
      document.body.appendChild(script)
    }
  }, [])

  // Reset to sign-in view whenever modal closes
  useEffect(() => {
    if (!open) setView('signin')
  }, [open])

  const handleSignInSuccess = useCallback((tokenDb: any) => {
    const tokenObj = {
      anonymous: tokenDb.anonymous,
      access_token: tokenDb.access_token || tokenDb.accessToken,
      refresh_token: tokenDb.refresh_token || tokenDb.refreshToken,
      token_number: tokenDb.token_number || tokenDb.tokenNumber,
      tokenNumber: tokenDb.token_number || tokenDb.tokenNumber,
      customer_id: tokenDb.customer_id || tokenDb.customerId,
      accessToken: tokenDb.access_token || tokenDb.accessToken,
      refreshToken: tokenDb.refresh_token || tokenDb.refreshToken,
      customerId: tokenDb.customer_id || tokenDb.customerId,
      ...(tokenDb.isSSO && { isSSO: tokenDb.isSSO }),
    }
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem('tokenStorage', JSON.stringify(tokenObj))
    }
    setToken(tokenObj)
    ;(setLoginTokenForMerging as (token: string) => void)(tokenObj.access_token)
    setProfile({
      email: tokenDb.email,
      firstname: tokenDb.firstname,
      lastname: tokenDb.lastname,
    })
    onOpenChange(false)
    window.location.reload()
  }, [setToken, setLoginTokenForMerging, setProfile, onOpenChange])

  const handleCreateAccountSuccess = useCallback((email: string) => {
    setVerificationEmail(email)
    setView('verification')
  }, [])

  const handleClose = useCallback(() => onOpenChange(false), [onOpenChange])

  if (view === 'verification') {
    return (
      <Modal
        open={open}
        onOpenChange={onOpenChange}
        header={VERIFICATION_HEADER}
        desktopWidth='400px'
        content={(
          <EmailVerification
            email={verificationEmail}
            onClose={handleClose}
          />
        )}
      />
    )
  }

  const isCreateAccount = view === 'createaccount'

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      header={isCreateAccount ? CREATE_ACCOUNT_HEADER : SIGNIN_HEADER}
      desktopWidth="500px"
      mobileMaxWidth="calc(100vw - 16px)"
      content={(
        isCreateAccount ? (
          <CreateAccountForm
            onSuccess={handleCreateAccountSuccess}
            onSwitchToSignIn={() => setView('signin')}
          />
        ) : (
          <SignInForm
            onSuccess={handleSignInSuccess}
            onSwitchToCreate={() => setView('createaccount')}
          />
        )
      )}
    />
  )
}

export default LoyaltySignInModal
